import {
  LoadingState,
  useAsyncEffect,
  useLoadingState,
  useMonkAppState,
  useObjectMemo,
} from '@monkvision/common';
import { useMonitoring } from '@monkvision/monitoring';
import { useMonkApi } from '@monkvision/network';
import { useState } from 'react';
import { OwnershipFilter, DashboardInspectionStatus } from '../useInspectionList/types';

export interface UseInspectionCountByStatusResult {
  counts: Record<DashboardInspectionStatus | 'all', number> | null;
  loading: LoadingState;
}

export interface InspectionCountByStatusParams {
  status: DashboardInspectionStatus | null;
  ownershipFilter: OwnershipFilter;
  triggerEffect: boolean;
}

const ALL_STATUSES: (DashboardInspectionStatus | 'all')[] = [
  'all',
  ...Object.values(DashboardInspectionStatus),
];

export function useInspectionCountByStatus({
  status,
  ownershipFilter,
  triggerEffect,
}: InspectionCountByStatusParams): UseInspectionCountByStatusResult {
  const [counts, setCounts] = useState<Record<DashboardInspectionStatus | 'all', number> | null>(
    null,
  );
  const loading = useLoadingState();
  const { handleError } = useMonitoring();
  const { config, authToken } = useMonkAppState();
  const apiConfig = {
    authToken: authToken ?? '',
    apiDomain: config.apiDomain,
    thumbnailDomain: config.thumbnailDomain,
  };
  const { getAllInspectionsCount } = useMonkApi(apiConfig);

  useAsyncEffect(
    () => {
      if (!counts) {
        loading.start();
      }
      const inspectionsPromise = ALL_STATUSES.map((value) =>
        getAllInspectionsCount({
          filters: {
            ...(value === 'all' ? {} : { customer_status: value }),
            ownership_filter: ownershipFilter,
          },
        }).then(({ count }) => ({ status: value, count })),
      );
      return Promise.all(inspectionsPromise);
    },
    [status, triggerEffect],
    {
      onResolve: (responses) => {
        const newCounts = responses.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.status]: curr.count,
          }),
          {} as Record<DashboardInspectionStatus | 'all', number>,
        );
        setCounts(newCounts);
        loading.onSuccess();
      },
      onReject: (err) => {
        handleError(err);
        loading.onError(err);
      },
    },
  );

  return useObjectMemo({ counts, loading });
}
